import { Injectable } from '@angular/core';
import { KalSnackbarConfig, KalSnackbarService } from '@kalidea/kaligraphi';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import gql from 'graphql-tag';
import { EMPTY, forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { IGNORE_ALL_GRAPHQL_ERRORS } from 'src/app/_core/graphql.module';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import {
    ResetSharedPaymentMethodsStateAction,
    SharedDepositsGetAFPAAccountIdAction,
    SharedPaymentMethodGetCollectifAccountWithInvoicesIdsListAction,
    SharedPaymentMethodGetPaymentMethodsFromInvoicesIdsListAction,
    SharedPaymentMethodsGetBankAccountWithInvoicesIdsListAction,
    SharedPaymentMethodsGetBanksListAction,
    SharedPaymentMethodsGetDefaultBankAccountIdForActivityAction,
    SharedPaymentMethodsGetIbansListAction,
    SharedPaymentMethodsGetLastBankForTiersAction,
    SharedPaymentMethodsInitPaymentMethodsAction
} from 'src/app/_shared/stores/settlements/shared-payment-methods/shared-payment-methods.action';
import { ActivityPaymentFragment } from 'src/app/board/activity/fragments/activity-payment.fragment';
import { graphqlErrors } from 'src/app/utils/errors.utils';
import {
    ArrayOfMoyenPaiementDto,
    ArrayOfRibDto,
    ArrayOfstring,
    BenefDto,
    FournisseurDto,
    JournalDto,
    MoyenPaiementDto,
    MutationGetCptCollectifClientActiviteWithListIdFactRglArgs,
    MutationGetIdCompteAfpaArgs,
    MutationGetIdCptBancaireParDefautSurActiviteArgs,
    MutationGetIdCptBancaireWithListIdFactArgs,
    MutationGetInitDataForReglementArgs,
    MutationGetLastBanqueForTiersArgs,
    MutationGetListeBanquesFromMouvementComptableArgs,
    MutationGetListeMoyenPaiementCommunForListeFacturesArgs,
    MutationGetListRibGeneriqueArgs,
    RibDto,
    TypeAfpaEnum
} from 'src/graphql/generated-types';

export class SharedPaymentMethodsStateModel {

    loading = false;

    paymentMethodsList: MoyenPaiementDto[] = [];

    journalsList: JournalDto[] = [];

    banksList: string[] = [];

    defaultBankAccountId: number = null;

    defaultCollectifAccountId: number = null;

    third: BenefDto | FournisseurDto = null;

    ibansList: RibDto[] = [];

    depositAccountId: number = null;

    lastSelectedBankName = '';

}

@Injectable()
@State<SharedPaymentMethodsStateModel>({
    name: 'sharedPaymentMethods',
    defaults: new SharedPaymentMethodsStateModel()
})
export class SharedPaymentMethodsState {

    constructor(private readonly graphqlService: GraphqlService,
                private readonly snackbarService: KalSnackbarService) {
    }

    // <editor-fold defaultstate="collapsed" desc="Selectors">

    @Selector()
    public static loading(state: SharedPaymentMethodsStateModel): boolean {
        return state.loading;
    }

    @Selector()
    public static defaultBankAccountId(state: SharedPaymentMethodsStateModel): number {
        return state?.defaultBankAccountId;
    }

    @Selector()
    public static defaultCollectifAccountId(state: SharedPaymentMethodsStateModel): number {
        return state?.defaultCollectifAccountId;
    }

    @Selector()
    public static paymentMethodsList(state: SharedPaymentMethodsStateModel): MoyenPaiementDto[] {
        return state?.paymentMethodsList || [];
    }

    @Selector()
    public static journalsList(state: SharedPaymentMethodsStateModel): JournalDto[] {
        return state?.journalsList || [];
    }

    @Selector()
    public static banksList(state: SharedPaymentMethodsStateModel): string[] {
        return state?.banksList || [];
    }

    @Selector()
    public static third(state: SharedPaymentMethodsStateModel): BenefDto | FournisseurDto {
        return state?.third;
    }

    @Selector()
    public static ibansList(state: SharedPaymentMethodsStateModel): RibDto[] {
        return state?.ibansList || [];
    }

    @Selector()
    public static lastSelectedBankName(state: SharedPaymentMethodsStateModel): string {
        return state?.lastSelectedBankName;
    }

    @Selector()
    public static depositAccountId(state: SharedPaymentMethodsStateModel): number {
        return state?.depositAccountId;
    }

    // </editor-fold>

    // <editor-fold defaultstate="collapsed" desc="Actions">

    @Action(SharedDepositsGetAFPAAccountIdAction)
    getAFPAAccountId(
        { patchState }: StateContext<SharedPaymentMethodsStateModel>,
        { params: { supplierOrClientContext, activityId } }: SharedDepositsGetAFPAAccountIdAction
    ): Observable<number> {
        return this.graphqlService.mutateOne<'GetIdCompteAFPA', MutationGetIdCompteAfpaArgs>({
            mutation: gql`
                mutation GetIdCompteAfpa(
                    $contexte: ContextInfosInput,
                    $contexteCF: ContexteClientFournisseurEnum,
                    $idActivite: Float,
                    $typeAFPA: TypeAFPAEnum
                ) {
                    GetIdCompteAFPA(contexte: $contexte, contexteCF: $contexteCF, idActivite: $idActivite,typeAFPA: $typeAFPA)
                }
            `,
            variables: {
                contexteCF: supplierOrClientContext,
                idActivite: activityId,
                typeAFPA: TypeAfpaEnum.Activite
            }
        }).pipe(
            tap(GetIdCompteAFPA => patchState({ depositAccountId: GetIdCompteAFPA })),
            catchError(err => {
                const code = +graphqlErrors(err, 'code');
                const isBusinessException = code === 1;

                if (isBusinessException) {
                    // Fix #53520 - la snackbar d'erreur est affichée automatiquement
                    return of(null);
                }

                return throwError(err);
            })
        );
    }

    @Action(SharedPaymentMethodsGetDefaultBankAccountIdForActivityAction)
    getDefaultBankAccountIdForActivity(
        { patchState }: StateContext<SharedPaymentMethodsStateModel>,
        { activityId }: SharedPaymentMethodsGetDefaultBankAccountIdForActivityAction
    ): Observable<number> {
        return this.graphqlService.mutateOne<'GetIdCptBancaireParDefautSurActivite', MutationGetIdCptBancaireParDefautSurActiviteArgs>({
            mutation: gql`
                mutation GetIdCptBancaireParDefautSurActivite($contexte: ContextInfosInput, $idActivite: Float) {
                    GetIdCptBancaireParDefautSurActivite(contexte: $contexte, idActivite: $idActivite)
                }
            `,
            variables: {
                idActivite: activityId,
            }
        }).pipe(
            tap(GetIdCptBancaireParDefautSurActivite =>
                patchState({ defaultBankAccountId: GetIdCptBancaireParDefautSurActivite || null })
            ),
            catchError(err => {
                return throwError(err);
            })
        );
    }

    @Action(SharedPaymentMethodsInitPaymentMethodsAction)
    initPaymentMethods(
        { patchState, dispatch }: StateContext<SharedPaymentMethodsStateModel>,
        {
            initParams: {
                beneficiaryCriterias,
                supplierCriterias,
                paymentCriterias,
            }
        }: SharedPaymentMethodsInitPaymentMethodsAction
    ): Observable<any> {
        patchState({ loading: true });

        let beneficiaryId = null;
        let supplierId = null;

        return this.graphqlService.mutateOne<'GetInitDataForReglement', MutationGetInitDataForReglementArgs>({
            mutation: gql`
                mutation GetInitDataForReglement(
                    $contexte: ContextInfosInput,
                    $CriteresBenef: CriteresBenefDTOInput,
                    $criteresFour: CriteresFournisseurDTOInput,
                    $CriterePaiMoy: CriteresMoyenPaiementDTOInput
                ) {
                    GetInitDataForReglement(
                        contexte: $contexte
                        CriteresBenef: $CriteresBenef
                        criteresFour: $criteresFour
                        CriterePaiMoy: $CriterePaiMoy
                    ){
                        ListeMoyenPaiement {
                            MoyenPaiementDTO {
                                Id
                                Code
                                Libelle
                                IsFiduciaire
                                TypeMoyenPaiement {
                                    Code
                                    Id
                                }
                                Article {
                                   ... article
                                }
                            }
                        }
                        ListeJournaux {
                            JournalDTO {
                                ... journal
                            }
                        }
                        ListeBeneficiaire {
                            BenefDTO {
                                Id
                                Nom
                                Prenom
                                Ribs {
                                    RibDTO {
                                        ...iban
                                    }
                                },
                                IdcOd
                                CompteTiers {
                                    FonctionneCredit
                                    FonctionneDebit
                                    Id
                                    IsFonctionnement
                                    IsSocial
                                },
                                CompteMembre
                                CodeMembre
                            }
                        }
                        ListeFournisseur {
                            FournisseurDTO {
                                Id
                                Nom
                                Ribs {
                                    RibDTO {
                                        ...iban
                                    }
                                }
                            }
                        }
                    }
                }
                ${ActivityPaymentFragment.journal}
                ${ActivityPaymentFragment.iban}
                ${ActivityPaymentFragment.article}
            `,
            variables: {
                CriteresBenef: beneficiaryCriterias || null,
                criteresFour: supplierCriterias || null,
                CriterePaiMoy: paymentCriterias || null
            }
        }).pipe(
            tap((GetInitDataForReglement) => {
                let third: BenefDto | FournisseurDto;

                if (beneficiaryCriterias) {
                    third = (GetInitDataForReglement?.ListeBeneficiaire?.BenefDTO?.[0] || null) as BenefDto;

                    third.CompteMembre = third?.CompteMembre || 0;

                    supplierId = null;
                    beneficiaryId = third.Id  || null;
                } else {
                    third = GetInitDataForReglement?.ListeFournisseur?.FournisseurDTO?.[0] || null;

                    beneficiaryId = null;
                    supplierId = supplierCriterias?.Id || null;
                }

                patchState({
                    paymentMethodsList: GetInitDataForReglement?.ListeMoyenPaiement?.MoyenPaiementDTO || [],
                    journalsList: GetInitDataForReglement?.ListeJournaux?.JournalDTO || [],
                    third,
                    ibansList: (third.Ribs?.RibDTO || []).filter((r: RibDto) => !r.EstCloture)
                });
            }),
            switchMap(() => {
                const actions: any[] = [
                    new SharedPaymentMethodsGetBanksListAction(),
                    new SharedPaymentMethodsGetLastBankForTiersAction(beneficiaryId || supplierId),
                    new SharedPaymentMethodsGetDefaultBankAccountIdForActivityAction(paymentCriterias.IdActivite),
                ];

                return forkJoin(dispatch(actions));
            }),
            tap(() => patchState({ loading: false })),
            catchError(err => {
                patchState({ loading: false });
                return throwError(err);
            })
        );
    }

    @Action(SharedPaymentMethodGetCollectifAccountWithInvoicesIdsListAction)
    getCollectifClient(
        { patchState }: StateContext<SharedPaymentMethodsStateModel>,
        { invoicesIdList }: SharedPaymentMethodGetCollectifAccountWithInvoicesIdsListAction
    ): Observable<number> {
        patchState({ loading: true });

        return this.graphqlService.mutateOne<'GetCptCollectifClientActiviteWithListIdFactRgl', MutationGetCptCollectifClientActiviteWithListIdFactRglArgs>({
            mutation: gql`
                mutation GetCptCollectifClientActiviteWithListIdFactRgl($contexte: ContextInfosInput, $ListIDFact: String) {
                    GetCptCollectifClientActiviteWithListIdFactRgl(contexte: $contexte, ListIDFact: $ListIDFact)
                }
            `,
            variables: {
                ListIDFact: invoicesIdList.join(',')
            }
        }).pipe(
            tap(GetCptCollectifClientActiviteWithListIdFactRgl =>
                patchState({
                    defaultCollectifAccountId: GetCptCollectifClientActiviteWithListIdFactRgl,
                    loading: false
                })
            ),
            catchError(err => {
                patchState({ loading: false });
                return throwError(err);
            })
        );
    }

    @Action(SharedPaymentMethodsGetBankAccountWithInvoicesIdsListAction)
    getBankAccountWithInvoicesIdsList(
        { patchState }: StateContext<SharedPaymentMethodsStateModel>,
        { invoicesIdList }: SharedPaymentMethodGetCollectifAccountWithInvoicesIdsListAction
    ): Observable<any> {
        patchState({ loading: true });

        return this.graphqlService.mutateOne<'GetIdCptBancaireWithListIdFact', MutationGetIdCptBancaireWithListIdFactArgs>({
            mutation: gql`
                mutation GetIdCptBancaireWithListIdFact ($contexte: ContextInfosInput, $listeIdFact: String) {
                    GetIdCptBancaireWithListIdFact(contexte: $contexte, listeIdFact: $listeIdFact)
                }
            `,
            variables: {
                listeIdFact: invoicesIdList.join(',')
            },
            context: {
                [IGNORE_ALL_GRAPHQL_ERRORS]: true
            }
        }).pipe(
            tap(GetIdCptBancaireWithListIdFact =>
                patchState({
                    defaultBankAccountId: GetIdCptBancaireWithListIdFact,
                    loading: false
                })
            ),
            catchError(err => {
                const message = graphqlErrors(err);
                const code = +graphqlErrors(err, 'code');

                patchState({
                    loading: false,
                    defaultCollectifAccountId: null
                });

                if (code !== 0) {
                    this.snackbarService.open(new KalSnackbarConfig({title: message}));
                    return throwError(err);
                }

                return EMPTY;
            })
        );
    }

    @Action(SharedPaymentMethodGetPaymentMethodsFromInvoicesIdsListAction)
    getPaymenMethodsFormInvoicesList(
        { patchState }: StateContext<SharedPaymentMethodsStateModel>,
        { invoicesIdList, paymentType }: SharedPaymentMethodGetPaymentMethodsFromInvoicesIdsListAction
    ): Observable<ArrayOfMoyenPaiementDto> {
        patchState({ loading: true });

        return this.graphqlService.mutateOne<'GetListeMoyenPaiementCommunForListeFactures', MutationGetListeMoyenPaiementCommunForListeFacturesArgs>({
            mutation: gql`
                mutation GetListeMoyenPaiementCommunForListeFactures (
                    $contexte: ContextInfosInput,
                    $listeIdFactures: ArrayOfdecimalInput,
                    $critere: CriteresMoyenPaiementDTOInput
                ) {
                    GetListeMoyenPaiementCommunForListeFactures(
                        contexte: $contexte,
                        listeIdFactures: $listeIdFactures,
                        critere: $critere
                    ) {
                        MoyenPaiementDTO {
                            Id
                            Code
                            Libelle
                            IsFiduciaire
                            TypeMoyenPaiement {
                                Id
                                Code
                            }
                            Article {
                                ... article
                            }
                        }
                    }
                }
                ${ActivityPaymentFragment.article}
            `,
            variables: {
                listeIdFactures: {
                    decimal: invoicesIdList
                },
                critere: {
                    IsVenteInActivite: paymentType === 'credit',
                    IsAchatInActivite: paymentType === 'debit',
                    TakeArticleWithStock: true,
                    TakeTypeMoyenPaiement: true,
                    IsClos: false
                }
            }
        }).pipe(
            tap(GetListeMoyenPaiementCommunForListeFactures =>
                patchState({
                    paymentMethodsList: GetListeMoyenPaiementCommunForListeFactures?.MoyenPaiementDTO || [],
                    loading: false
                })
            ),
            catchError(err => {
                patchState({ loading: false });
                return throwError(err);
            })
        );
    }

    @Action(SharedPaymentMethodsGetBanksListAction)
    getBanksList({ patchState }: StateContext<SharedPaymentMethodsStateModel>): Observable<ArrayOfstring> {

        return this.graphqlService.mutateOne<'GetListeBanquesFromMouvementComptable', MutationGetListeBanquesFromMouvementComptableArgs>({
            mutation: gql`
                mutation GetListeBanquesFromMouvementComptable($contexte: ContextInfosInput) {
                    GetListeBanquesFromMouvementComptable(contexte: $contexte) {
                        string
                    }
                }
            `
        }).pipe(
            tap(GetListeBanquesFromMouvementComptable =>
                patchState({ banksList: GetListeBanquesFromMouvementComptable?.string || [] })
            ),
            catchError(err => {
                return throwError(err);
            })
        );
    }

    @Action(SharedPaymentMethodsGetIbansListAction)
    getIbansList(
        { patchState }: StateContext<SharedPaymentMethodsStateModel>,
        { criterias }: SharedPaymentMethodsGetIbansListAction
    ): Observable<ArrayOfRibDto> {
        return this.graphqlService.mutateOne<'GetListRIBGenerique', MutationGetListRibGeneriqueArgs>({
            mutation: gql`
                mutation GetListRIBGenerique($contexte: ContextInfosInput, $critere: CriteresRibDTOInput) {
                    GetListRIBGenerique(contexte: $contexte, critere: $critere) {
                        RibDTO {
                            ... iban
                        }
                    }
                }
                ${ActivityPaymentFragment.iban}
            `,
            variables: {
                critere: criterias
            }
        }).pipe(
            tap(GetListRIBGenerique =>
                patchState({ ibansList: GetListRIBGenerique?.RibDTO || [] })
            ),
            catchError(err => {
                return throwError(err);
            })
        );
    }

    @Action(SharedPaymentMethodsGetLastBankForTiersAction)
    getLastBankForTiers(
        { patchState }: StateContext<SharedPaymentMethodsStateModel>,
        { idBeneficiaire, idFournisseur }: SharedPaymentMethodsGetLastBankForTiersAction
    ): Observable<string> {
        return this.graphqlService.mutateOne<'GetLastBanqueForTiers', MutationGetLastBanqueForTiersArgs>({
            mutation: gql`
                mutation GetLastBanqueForTiers(
                    $contexte: ContextInfosInput,
                    $idBeneficiaire: Float,
                    $idFournisseur: Float
                ){
                    GetLastBanqueForTiers(
                        contexte: $contexte,
                        idBeneficiaire: $idBeneficiaire,
                        idFournisseur: $idFournisseur
                    )
                }
            `,
            variables: {
                idBeneficiaire,
                idFournisseur
            }
        }).pipe(
            tap(GetLastBanqueForTiers =>
                patchState({ lastSelectedBankName: GetLastBanqueForTiers || '' })
            )
        );
    }

    // </editor-fold>

    @Action(ResetSharedPaymentMethodsStateAction)
    resetState({ patchState }: StateContext<SharedPaymentMethodsStateModel>): void {
        patchState(new SharedPaymentMethodsStateModel());
    }
}
